import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions';
import AdminLayout from '../../layouts/AdminLayout';
import SubNavbar from '../../components/ui/SubNavbar';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Switch from 'react-switch';
import notify from '../../utils/IANotification';
import { MdArrowBack, MdSave, MdDelete } from 'react-icons/md';
import { fetch_location_by_id, create_location, update_location, delete_location } from '../../api/locationsAPI';

export default function LocationFormPage() {
    const navigate = useNavigate();
    useFetchAuthorisation("user");
    useFetchUserData();

    const location_id = new URLSearchParams(window.location.search).get("location_id") || "new";
    const [activeTab, setActiveTab] = useState("general");

    const [locationData, setLocationData] = useState({
        name: "",
        description: "",
        capacity: 0,
        location_type: "Hall",
        is_booking_enabled: "1",
        is_active: "1",
    });

    const tabs = [
        { id: "general", label: "General Details" },
        { id: "settings", label: "Booking & Availability" },
    ];

    const loadLocationData = async () => {
        if (location_id === "new") return;
        const res = await fetch_location_by_id(location_id);
        if (res?.status === 200) {
            setLocationData(res.data || {});
        }
    };

    useEffect(() => {
        loadLocationData();
    }, [location_id]);

    const handleSave = async () => {
        if (!locationData.name) {
            return notify({ type: "error", message: "Please enter a location name" });
        }

        if (location_id === "new") {
            const res = await create_location(locationData);
            if (res?.status === 200) {
                notify({ type: "success", message: "Location created successfully" });
                navigate(`/admin/locations/details?location_id=${res.data.id}`);
            } else {
                notify({ type: "error", message: res?.message || "Failed to create location" });
            }
        } else {
            const res = await update_location(location_id, locationData);
            if (res?.status === 200) {
                notify({ type: "success", message: "Location updated successfully" });
                loadLocationData();
            } else {
                notify({ type: "error", message: res?.message || "Failed to update location" });
            }
        }
    };

    const handleDelete = async () => {
        if (location_id === "new") return;
        const res = await delete_location(location_id);
        if (res?.status === 200) {
            notify({ type: "success", message: "Location deleted successfully" });
            navigate('/admin/locations');
        } else {
            notify({ type: "error", message: res?.message || "Failed to delete location" });
        }
    };

    return (
        <AdminLayout
            navSelected={"Locations & Facilities"}
            headerText={location_id === "new" ? "New Location" : "Edit Location"}
            toolbar={
                <>
                    <div className="flex items-center gap-3">
                        <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/locations')}>
                            Back
                        </Button>
                        <h1 className="text-xl font-bold text-gray-900">
                            {location_id === "new" ? "Create Location" : locationData.name || "Edit Location"}
                        </h1>
                    </div>
                    <div className="flex gap-2">
                        {location_id !== "new" && (
                            <Button variant="danger" icon={MdDelete} onClick={handleDelete}>
                                Delete
                            </Button>
                        )}
                        <Button variant="primary" icon={MdSave} onClick={handleSave}>
                            {location_id === "new" ? "Create" : "Save Changes"}
                        </Button>
                    </div>
                </>
            }
        >
            <SubNavbar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

            {/* TAB 1: GENERAL DETAILS */}
            {activeTab === "general" && (
                <Card title="Facility Details" subtitle="Name, category type, description, and room capacity.">
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="input-container md:col-span-2">
                                <label className="input-label font-semibold">Location / Facility Name</label>
                                <input
                                    type="text"
                                    className="text-input"
                                    placeholder="e.g. Main Sanctuary Level 2"
                                    value={locationData.name || ""}
                                    onChange={(e) => setLocationData({ ...locationData, name: e.target.value })}
                                />
                            </div>

                            <div className="input-container">
                                <label className="input-label font-semibold">Facility Category</label>
                                <select
                                    className="select-input"
                                    value={locationData.location_type || "Hall"}
                                    onChange={(e) => setLocationData({ ...locationData, location_type: e.target.value })}
                                >
                                    <option value="Hall">Hall</option>
                                    <option value="Sanctuary">Sanctuary</option>
                                    <option value="Classroom">Classroom / Meeting Room</option>
                                    <option value="Cafeteria">Cafeteria / Pantry</option>
                                    <option value="Outdoor">Outdoor Court / Lawn</option>
                                </select>
                            </div>
                        </div>

                        <div className="input-container max-w-xs">
                            <label className="input-label font-semibold">Maximum Seating / Capacity</label>
                            <input
                                type="number"
                                className="text-input"
                                placeholder="500"
                                value={locationData.capacity || 0}
                                onChange={(e) => setLocationData({ ...locationData, capacity: e.target.value })}
                            />
                        </div>

                        <div className="input-container">
                            <label className="input-label font-semibold">Facility Overview &amp; Amenities</label>
                            <textarea
                                className="textarea-input h-32"
                                placeholder="e.g. Equipped with AV system, projector screen, and air conditioning..."
                                value={locationData.description || ""}
                                onChange={(e) => setLocationData({ ...locationData, description: e.target.value })}
                            />
                        </div>
                    </div>
                </Card>
            )}

            {/* TAB 2: BOOKING & AVAILABILITY */}
            {activeTab === "settings" && (
                <Card title="Booking &amp; Status Controls" subtitle="Configure whether users can book this facility.">
                    <div className="space-y-6">
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                            <div>
                                <label className="font-semibold text-sm text-gray-900">Enable Facility Booking</label>
                                <p className="text-xs text-gray-500">Allow users with permission to submit room booking requests for this location.</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-xs font-semibold text-gray-700">
                                    {locationData.is_booking_enabled === "1" ? "Booking Enabled" : "Booking Disabled"}
                                </span>
                                <Switch
                                    checked={locationData.is_booking_enabled === "1"}
                                    onChange={(checked) => setLocationData({ ...locationData, is_booking_enabled: checked ? "1" : "0" })}
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                            <div>
                                <label className="font-semibold text-sm text-gray-900">Facility Active Status</label>
                                <p className="text-xs text-gray-500">Toggle operational availability of this room/space.</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-xs font-semibold text-gray-700">
                                    {locationData.is_active === "1" ? "Active" : "Inactive / Closed"}
                                </span>
                                <Switch
                                    checked={locationData.is_active === "1"}
                                    onChange={(checked) => setLocationData({ ...locationData, is_active: checked ? "1" : "0" })}
                                />
                            </div>
                        </div>
                    </div>
                </Card>
            )}
        </AdminLayout>
    );
}
